import {
  Alert, Box, Chip, CircularProgress, Paper, Stack, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { featureFlagsApi, type FeatureFlagDto, type FeaturePhaseName } from "@/api/endpoints";
import { FEATURE_FLAGS_QUERY_KEY, useFeatureFlags } from "@/store/featureFlags";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

const PHASE_ORDER: FeaturePhaseName[] = ["Mvp1", "Mvp2", "Final"];

/**
 * Section labels intentionally describe what the flag bundle does, not which build phase
 * shipped it. The Mvp1/Mvp2/Final categorisation survives only as the backend tier metadata
 * (it decides defaults and whether the row is toggleable); the admin sees plain-English
 * groupings instead.
 */
const PHASE_LABEL: Record<FeaturePhaseName, string> = {
  Mvp1: "Baseline (always on)",
  Mvp2: "Optional capabilities",
  Final: "Advanced & integrations",
};

const PHASE_COLOR: Record<FeaturePhaseName, "primary" | "secondary" | "default"> = {
  Mvp1: "primary",
  Mvp2: "secondary",
  Final: "default",
};

/**
 * Admin screen for tenant feature flags. MVP 1 rows are read-only and chipped as
 * "baseline" — they cannot be turned off. MVP 2 / Final rows show a toggle switch that
 * calls <c>PUT /api/v1/feature-flags/{key}</c>. The sidebar listens to the same query so
 * groups appear / disappear inside a tick.
 */
export function FeatureFlagsPage() {
  const qc = useQueryClient();
  const flags = useFeatureFlags();
  const canManage = useAuth(s => s.hasPermission("system.feature_flag.manage"));

  const mutate = useMutation({
    mutationFn: async ({ key, enabled }: { key: string; enabled: boolean }) =>
      featureFlagsApi.set(key, enabled),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: FEATURE_FLAGS_QUERY_KEY });
    },
  });

  const grouped: Record<FeaturePhaseName, FeatureFlagDto[]> = {
    Mvp1: [], Mvp2: [], Final: [],
  };
  for (const r of flags.rows) {
    grouped[r.phase].push(r);
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }} spacing={2}>
        <Typography variant="h5" fontWeight={600}>Feature flags</Typography>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Optional and advanced features are hidden in the sidebar until the corresponding
        flag below is enabled. Baseline features are always on and are not toggleable.
        Every change writes an audit-log entry automatically.
      </Alert>

      {!canManage && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You can view feature-flag state but only an admin with
          <b> system.feature_flag.manage</b> can toggle them.
        </Alert>
      )}

      {mutate.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutate.error)}</Alert>
      )}
      {flags.isLoading && (
        <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      )}

      {PHASE_ORDER.map(phase => {
        const rows = grouped[phase];
        if (rows.length === 0) return null;
        return (
          <Paper key={phase} sx={{ mb: 2 }}>
            <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
              <Chip size="small" color={PHASE_COLOR[phase]} label={PHASE_LABEL[phase]} />
              <Typography variant="subtitle2" color="text.secondary">
                {rows.length} feature{rows.length === 1 ? "" : "s"}
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={260}>Feature key</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="center" width={140}>State</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(r => {
                    const isBaseline = r.phase === "Mvp1";
                    return (
                      <TableRow key={r.featureKey} hover>
                        <TableCell><code>{r.featureKey}</code></TableCell>
                        <TableCell>{r.description}</TableCell>
                        <TableCell align="center">
                          {isBaseline ? (
                            <Chip
                              size="small" icon={<LockIcon />}
                              color="success" label="Always on"
                            />
                          ) : (
                            <Switch
                              checked={r.isEnabled}
                              disabled={!canManage || mutate.isPending}
                              onChange={(e) =>
                                mutate.mutate({ key: r.featureKey, enabled: e.target.checked })}
                              inputProps={{
                                "aria-label": `Toggle ${r.featureKey}`,
                                ["data-testid" as never]: `feature-flag-toggle-${r.featureKey}`,
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        );
      })}
    </Box>
  );
}
